// What a pod that is "not here yet" is waiting on, drawn as its dependency
// graph, in the body of the job screen.
//
// Standing at a station with nothing to do, the question is "what is holding
// it and where". The route is not a line — Odoo records real dependencies, and
// a pod's door subassembly runs alongside its body and merges back in — so this
// is laid out like a commit graph, the same one the pod journey uses and read
// the same way: earliest work at the top, branches in their own lanes, and this
// station at the foot, where everything above it merges in. The rows joined to
// the foot are the direct blockers; the rest are the parallel streams that
// have to finish before those can.
//
// This used to be a sheet behind a "see what it is waiting on" button. The
// native shop floor app shows the unfinished predecessors the moment a pod is
// scanned, and the floor said the extra tap was a step backwards — so it is
// drawn where the checks would be, since the checks mean nothing until the
// pod arrives.
//
// Every row is a way in. Tapping one takes the operator to that work centre
// with this pod selected, because the next thing they want after seeing the
// hold-up is to go and look at it.

import type { JourneyStepVM, PodVM } from '@/api/types';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { GraphRows } from '@/components/pod/GraphRows';
import { buildUpstreamGraph } from '@/lib/upstreamGraph';
import { useT } from '@/i18n/useT';

export function WaitingOn({
  pod,
  journey,
  loading,
  onJumpTo,
}: {
  pod: PodVM;
  journey: JourneyStepVM[];
  /** The route has not arrived yet, so there is nothing to draw. */
  loading: boolean;
  /** Go to that work centre with this pod selected. */
  onJumpTo?: (step: JourneyStepVM) => void;
}) {
  const t = useT();
  const graph = buildUpstreamGraph(journey, pod.woId);
  const onRoute = journey.some((s) => s.woId === pod.woId);

  return (
    <section
      aria-label={t.waitingOn}
      style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: '12px 14px' }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <span className="sectionLabel" style={{ marginBottom: 0 }}>
          {t.waitingOn}
        </span>
        <span style={{ font: "700 12px 'Space Grotesk',sans-serif", color: 'var(--warn)', whiteSpace: 'nowrap' }}>
          {t.notHereYet}
        </span>
      </div>

      {loading ? (
        <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
          <Skeleton height={54} radius={10} />
          <Skeleton height={54} radius={10} />
        </div>
      ) : !onRoute ? (
        <EmptyState>{t.waitingOnUnknown}</EmptyState>
      ) : graph.nodes.length === 0 ? (
        <EmptyState>{t.waitingOnNothingUpstream}</EmptyState>
      ) : (
        <>
          <p style={{ font: '500 12.5px Manrope,sans-serif', color: 'var(--ink)', margin: '8px 0 2px' }}>
            {graph.total === 1 ? t.waitingOnOne : `${graph.total} ${t.waitingOnMany}`}
          </p>
          {onJumpTo && (
            <p style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)', margin: '0 0 10px' }}>
              {t.waitingOnTapHint}
            </p>
          )}

          {/* This station is the foot of the graph, so it reads towards the
              operator: everything above it has to happen first. */}
          <GraphRows graph={graph} foot={pod.workcentreName} {...(onJumpTo ? { onJumpTo } : {})} />
        </>
      )}
    </section>
  );
}
